import React, { useState, useEffect, useCallback } from 'react';
import Card from '../../components/common/Card/Card';
import Table from '../../components/common/Table/Table';
import StatusBadge from '../../components/common/StatusBadge/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getAllProjects } from '../../services/projectService';
import { getCompetitionsByProject } from '../../services/competitionService';
import { getAllMentors } from '../../services/mentorService';
import { getAllStudents } from '../../services/studentService';
import { formatDate } from '../../utils/helpers';
import styles from './Competitions.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const Competitions = () => {
  const [allProjects, setAllProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  const filterProjects = useCallback(() => {
    let filtered = [...allProjects];

    if (selectedMentor) {
      filtered = filtered.filter(p => p.mentor_id === selectedMentor);
    }

    if (selectedStudent) {
      filtered = filtered.filter(p => p.student_id === selectedStudent);
    }

    setFilteredProjects(filtered);
    
    if (selectedProject && !filtered.find(p => p.id === selectedProject)) {
      setSelectedProject('');
    }
  }, [allProjects, selectedMentor, selectedStudent, selectedProject]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [filterProjects]);

  useEffect(() => {
    if (selectedProject) {
      fetchCompetitions(selectedProject);
    } else {
      setCompetitions([]);
    }
  }, [selectedProject]);

  const fetchData = async () => {
    try {
      const [projectsData, mentorsData, studentsData] = await Promise.all([
        getAllProjects(),
        getAllMentors(),
        getAllStudents()
      ]);
      setAllProjects(projectsData);
      setFilteredProjects(projectsData);
      setMentors(mentorsData);
      setStudents(studentsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitions = async (projectId) => {
    try {
      const data = await getCompetitionsByProject(projectId);
      setCompetitions(data);
    } catch (error) {
      console.error('Error fetching competitions:', error);
    }
  };

  const columns = [
    { header: 'Name', accessor: 'competition_name' },
    { header: 'Type', accessor: 'submission_type', render: (row) => row.submission_type.toUpperCase() },
    { header: 'Submission Date', accessor: 'submission_date', render: (row) => formatDate(row.submission_date) },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { header: 'Result', accessor: 'result', render: (row) => row.result || '—' }
  ];

  if (loading) {
    return <div className={styles.loading}><LoadingSpinner /></div>;
  }

  return (
    <div className={styles.competitionsPage}>
      <h1>Competitions & Journal Submissions</h1>

      <div className={styles.filterSection}>
        <div className={styles.formGroup}>
          <label>Select Mentor</label>
          <select value={selectedMentor} onChange={(e) => setSelectedMentor(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {mentors.map(mentor => (
              <option key={mentor.id} value={mentor.id}>
                {mentor.first_name} {mentor.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Select Student</label>
          <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {students.map(student => (
              <option key={student.id} value={student.id}>
                {student.first_name} {student.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formGroup}>
          <label>Select Project</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)} className={styles.select}>
            <option value="">-- Select --</option>
            {sortProjectsByName(filteredProjects).map(project => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedProject ? (
        <Card>
          <Table columns={columns} data={competitions} />
        </Card>
      ) : (
        <Card>
          <p className={styles.empty}>Please select a project to view competitions</p>
        </Card>
      )}
    </div>
  );
};

export default Competitions;